import { Skeleton, Stack } from '@mui/material';
import React, { useEffect } from "react";
import { useRecoilValue } from 'recoil';
import { SalaryService } from '../../../../Component/Api/EmploymentEquity/Salary/SalaryService';
import { IFilter } from '../../../../Component/Common/Components/DataTable/Filters';
import { ColumnSortOption, SortDirections } from '../../../../Component/Model/Dto/ColumnSortOption';
import { SalaryDtos } from '../../../../Component/Model/Dto/Salaries';
import { ExportKeyValue } from "../../../../Component/Model/Dto/Upload";
import { DataImportType, EmployeeTypes, UserActivityActionType } from "../../../../Component/Model/Enum/BEE123Enums";
import { EntriesPerPage } from "../../../../Component/Model/Table/Table";
import { defaultObject } from "../../../../Component/Model/helpers";
import { ConsolidationAtom } from '../../../../State/Consolidation.Atom';
import { ImportExportButtonsDialog } from "../../../Common/ImportAndExport/ImportExportButtonsDialog";
import { IndividualSalaryModal } from '../../../Common/Individual/IndividualSalaryModal';
import { EmployeeModal } from "../EmployeeModal";
import { EmployeeSalariesConsolidationWithActionsTableMapper, EmployeeSalariesWithActionsTableMapper } from './EmployeeSalariesColumns';
import { EmployeeSalariesDataTable } from './EmployeeSalariesDataTable';
import _ from 'lodash';
import { UserActivityService } from '../../../../Component/Api/UserActivity/UserActivityService';
import { EmployeeSalariesSection } from '../../../../Component/Common/Constants/UserActivityTrackingSections';
import { EE123SalariesRole_Edit, UserAdminRole } from '../../../../Component/Common/Constants/UserRoles';
import { userRolesState } from '../../../../State/user.Atom';


interface IProps {
    scorecardId: string;
    hasFilters: boolean;
    employedAtDate: Date;
}

export const EmployeeSalariesTableWithActions = (props: IProps) => {
    const [employeeSalariesData, setEmployeeSalariesData] = React.useState<SalaryDtos.SalarySearchRow[]>(defaultObject);
    const [entriesPerPage, setEntriesPerPage] = React.useState<EntriesPerPage>({ defaultValue: 10 });
    const [filteredItemsCount, setFilteredItemsCount] = React.useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<number>(0);
    const [showIndividualModal, setShowIndividualModal] = React.useState<boolean>(false);
    const [showEmployeeModal, setShowEmployeeModal] = React.useState<boolean>(false);
    const isConsolidateScorecard = useRecoilValue(ConsolidationAtom);
    const editRoleAccess = [
        EE123SalariesRole_Edit,
        UserAdminRole
    ];
    const userRoles = useRecoilValue(userRolesState);
    const isEditable = userRoles.some(r => editRoleAccess.includes(r));
    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [columnSortOptions, setColumnSortOptions] = React.useState<ColumnSortOption[]>([]);
    const [sortColumnNames, setSortColumnNames] = React.useState<string[]>([]);
    const [sortDirections, setSortDirections] = React.useState<SortDirections[]>([]);
    const [filters, setFilters] = React.useState<any>();

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (searchValue: string) => {
        setSearchValue(searchValue);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const salariesLog = (actionType: number, actionDescription: string) => {
        UserActivityService.createActivityLog({
            section: EmployeeSalariesSection,
            actionType: actionType,
            actionDescription: actionDescription,
            scorecardId: props.scorecardId,
            requestUrl: window.location.href
        });
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let filteredRequest: SalaryDtos.SearchRequest = ({
                pageNumber: pageNumber,
                pageSize: entriesPerPage.defaultValue!,
                scorecardId: props.scorecardId,
                searchValue: searchValue,
                columnSortOptions: columnSortOptions,
                genderIds: [],
                occupationalLevelIds: [],
                raceIds: [],
                employeeTypeIds: [EmployeeTypes.Employee, EmployeeTypes.Employee_Previously_Unemployed],
                provinceIds: [],
                disabledValues: [],
                isYesEmployee: [],
                isYesAbsorbed: [],
                employedAtDate: props.employedAtDate
            });
            filteredRequest = { ...filteredRequest, ...filters };

            if (filteredRequest.isTemporary == null || filteredRequest.isTemporary == false) {
                filteredRequest = _.omit(filteredRequest, ["isTemporary"]);
            }

            const searchResult = await SalaryService.Search(filteredRequest);
            if (searchResult.data) {
                setEmployeeSalariesData(searchResult.data.results);
                setFilteredItemsCount(searchResult.data.filteredItemsCount);
                setTotalItemsCount(searchResult.data.totalItemsCount);

                salariesLog(UserActivityActionType.View, "View list of employee salaries");
            }
        } catch (error) {
            setEmployeeSalariesData(defaultObject);
            setFilteredItemsCount(1);
            setTotalItemsCount(1);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (id: number) => {
        setSelectedEmployeeId(id);
        setShowIndividualModal(true);
    }

    const closeIndividualModal = () => {
        setShowIndividualModal(false);
        setSelectedEmployeeId(0);
        fetchData();
    }

    const closeEmployeeModal = () => {
        setShowEmployeeModal(false);
        setSelectedEmployeeId(0);
        fetchData();
    }

    const afterEmployeeSave = (id: number) => {
        handleEdit(id);
        setShowEmployeeModal(false);
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue, sortColumnNames, sortDirections, filters]); // eslint-disable-line react-hooks/exhaustive-deps

    const employeeSalariesFilters: Array<IFilter> = [
        {
            label: 'Gender:',
            values: [{ key: 'Male', val: 1 }, { key: 'Female', val: 2 }],
            type: 1, 
            propname: 'genderIds',
        },
        {
            label: 'Race:',
            values: [{ key: 'African', val: 1 }, { key: 'Coloured', val: 2 }, { key: 'Indian', val: 3 }, { key: 'White', val: 4 }, { key: 'Non South African', val: 5 }],
            type: 1, 
            propname: 'raceIds',
        },
        {
            label: 'Occupation Level:',
            values: [{ key: 'Executive Director', val: 1 },
            { key: 'Other Executive Management', val: 4 },
            { key: 'Senior Management', val: 5 },
            { key: 'Middle Management', val: 6 },
            { key: 'Junior Management', val: 7 },
            { key: 'Semi skilled', val: 8 },
            { key: 'Unskilled', val: 9 }],
            type: 1, 
            propname: 'occupationalLevelIds'
        },
        {
            label: 'Disabled:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: 1, 
            propname: 'disabledValues'
        },
        {
            label: 'Province:',
            values: [
                { key: 'Unspecified', val: 0 },
                { key: 'National (NAT)', val: 1 },
                { key: 'Eastern Cape (EC)', val: 2 },
                { key: 'Free State (FS)', val: 3 },
                { key: 'Gauteng (GP)', val: 4 },
                { key: 'KwaZulu-Natal (KZN)', val: 5 },
                { key: 'Limpopo (LP)', val: 6 },
                { key: 'Mpumalanga (MP)', val: 7 },
                { key: 'North West (NW)', val: 8 },
                { key: 'Northern Cape (NC)', val: 9 },
                { key: 'Western Cape (WC)', val: 10 },
            ],
            type: 1, 
            propname: 'provinceTypeIds'
        },
        {
            label: 'Y.E.S. Employee:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: 1, 
            propname: 'isYesEmployee'

        },
        {
            label: 'Y.E.S. Absorbed:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: 1, 
            propname: 'isYesAbsorbed'
        },
        {
            label: 'Age Range',
            values: [],
            type: 6, 
            propname: 'AgeRangeFilter',
        },
        {
            label: 'Only include temporary employees',
            values: [],
            type: 7, 
            propname: 'isTemporary',
        }
    ];

    const filtersChanged = (filterProps: any) => {
        let AgeRangeFilterMinimum = filterProps['AgeRangeFilter']?.minimum;
        let AgeRangeFilterMaximum = filterProps['AgeRangeFilter']?.maximum;

        setFilters({ ...filterProps, AgeRangeFilterMinimum, AgeRangeFilterMaximum });
    }

    let employeeTableData = undefined;
    if (isConsolidateScorecard) {
        employeeTableData = EmployeeSalariesConsolidationWithActionsTableMapper(employeeSalariesData, handleEdit, isEditable);
    } else {
        employeeTableData = EmployeeSalariesWithActionsTableMapper(employeeSalariesData, handleEdit, isEditable);
    }

    const parameters: ExportKeyValue[] = [
    ];

    if (employeeTableData !== undefined) {
        return (
            <>
                <EmployeeSalariesDataTable
                    employeeSalariesData={employeeTableData}
                    entriesPerPage={entriesPerPage}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemsCount}
                    totalItemsCount={totalItemsCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    searchValueChanged={searchValueChanged}
                    columnSortingChanged={columnSortingChanged}
                    isLoading={isLoading}
                    hasFilters={props.hasFilters}
                    employeeSalariesFilters={employeeSalariesFilters}
                    filtersChanged={filtersChanged}
                />

                {showIndividualModal && <IndividualSalaryModal showIndividualModal={showIndividualModal} closeIndividualModal={closeIndividualModal} scorecardId={props.scorecardId} id={selectedEmployeeId} section={EmployeeSalariesSection}/>}

                {showEmployeeModal && <EmployeeModal scorecardId={props.scorecardId} person={null} afterSaved={afterEmployeeSave} showEmployeeModal={showEmployeeModal} closeEmployeeModal={closeEmployeeModal} />}

                {<ImportExportButtonsDialog scorecardId={props.scorecardId}
                    uploadType={DataImportType[DataImportType.EESalary]}
                    controlsEnabled={true}
                    showHelpfileDownload={true}
                    importBus={false}
                    coreExportEnabled={true}
                    coreImportEnabled={true}
                    disableImport={!isEditable}
                    type={"EESalary"}
                    typeId={DataImportType.EESalary}
                    parameters={parameters}
                    reloadTable={fetchData}
                    showYearToAdd={true}
                    showAdditionalParamsModal={false} 
                    section="Employee Salaries"/>}
            </>
        )
    } else {
        return (
            <Stack spacing={1}>
                <Skeleton variant="text" />
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="rectangular" width={210} height={118} />
            </Stack>
        )
    }
}
